import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  Paper,
  Typography,
} from '@mui/material';
import { ProfileEditForm, PasswordChangeForm, DeactivateAccountModal } from '../../components/features/user';
import { useAuth } from '../../hooks';
import { userService } from '../../services';
import type { UserUpdate, PasswordChange } from '../../types';

export function ProfilePage() {
  const navigate = useNavigate();
  const { user, logout, fetchUser } = useAuth();

  // Profile edit state
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSuccess, setProfileSuccess] = useState(false);

  // Password change state
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // Deactivate modal state
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [deactivateLoading, setDeactivateLoading] = useState(false);
  const [deactivateError, setDeactivateError] = useState<string | null>(null);

  const handleProfileSubmit = async (data: UserUpdate) => {
    setProfileLoading(true);
    setProfileError(null);
    setProfileSuccess(false);
    try {
      await userService.updateMe(data);
      await fetchUser();
      setProfileSuccess(true);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setProfileError(error.response?.data?.detail || '프로필 수정에 실패했습니다');
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordSubmit = async (data: PasswordChange) => {
    setPasswordLoading(true);
    setPasswordError(null);
    setPasswordSuccess(false);
    try {
      await userService.changePassword(data);
      setPasswordSuccess(true);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setPasswordError(error.response?.data?.detail || '비밀번호 변경에 실패했습니다');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleDeactivateConfirm = async (password: string) => {
    setDeactivateLoading(true);
    setDeactivateError(null);
    try {
      await userService.deactivateAccount({ password });
      await logout();
      navigate('/login');
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setDeactivateError(error.response?.data?.detail || '계정 비활성화에 실패했습니다');
    } finally {
      setDeactivateLoading(false);
    }
  };

  if (!user) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">사용자 정보를 불러올 수 없습니다</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        내 계정
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <ProfileEditForm
          initialValues={{ name: user.name, email: user.email }}
          onSubmit={handleProfileSubmit}
          isLoading={profileLoading}
          error={profileError}
          success={profileSuccess}
        />

        <PasswordChangeForm
          onSubmit={handlePasswordSubmit}
          isLoading={passwordLoading}
          error={passwordError}
          success={passwordSuccess}
        />

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom color="error">
            위험 구역
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            계정을 비활성화하면 더 이상 로그인할 수 없습니다. 이 작업은 관리자만 되돌릴 수 있습니다.
          </Typography>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setDeactivateOpen(true)}
          >
            계정 비활성화
          </Button>
        </Paper>
      </Box>

      <DeactivateAccountModal
        open={deactivateOpen}
        onClose={() => {
          setDeactivateOpen(false);
          setDeactivateError(null);
        }}
        onConfirm={handleDeactivateConfirm}
        isLoading={deactivateLoading}
        error={deactivateError}
      />
    </Container>
  );
}
